import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaSpinner, FaEdit, FaEye, FaTrash, FaKey } from 'react-icons/fa';
import axios from 'axios';
import ResetPasswordButton from '../components/ResetPasswordButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client? This will delete all related data.')) {
      try {
        await axios.delete(`${API}/clients/${clientId}`);
        setClients(clients.filter(c => c.id !== clientId));
      } catch (error) {
        alert('Failed to delete client');
      }
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.contact && client.contact.includes(searchTerm))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <FaSpinner className="animate-spin text-[#FFD700]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Clients</h1>
          <p className="text-gray-400 text-sm sm:text-base">Manage your client profiles and financial plans</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/clients/new')}
          className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all transform hover:scale-105 text-sm sm:text-base"
        >
          <FaPlus />
          <span>Add New Client</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-4 sm:p-6 border border-[#2D5A8F]">
        <div className="relative">
          <FaSearch className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all text-sm sm:text-base"
          />
        </div>
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 sm:p-12 border border-[#2D5A8F] text-center">
          <p className="text-gray-400 text-base sm:text-lg">No clients found. Add your first client to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-4 sm:p-6 border border-[#2D5A8F] shadow-lg hover:shadow-2xl transition-all hover:border-[#FFD700]"
            >
              {/* Client Info */}
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1 truncate">{client.name}</h3>
                  <p className="text-gray-400 text-xs sm:text-sm truncate">{client.profile_occupation || 'No occupation specified'}</p>
                </div>
                {client.photo_url && (
                  <img src={client.photo_url} alt={client.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-[#FFD700] ml-2 flex-shrink-0" />
                )}
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4">
                {client.age && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Age:</span>
                    <span className="text-white font-medium">{client.age} years</span>
                  </div>
                )}
                {client.contact && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Phone:</span>
                    <span className="text-white font-medium">{client.contact}</span>
                  </div>
                )}
                {client.spouse_name && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Spouse:</span>
                    <span className="text-white font-medium">{client.spouse_name}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Dependents:</span>
                  <span className="text-white font-medium">{client.total_dependents}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Children:</span>
                  <span className="text-white font-medium">{client.children?.length || 0}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 pt-4 border-t border-[#2D5A8F]">
                <button
                  onClick={() => navigate(`/dashboard/clients/${client.id}/view`)}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F] transition-all"
                >
                  <FaEye />
                  <span>View Plan</span>
                </button>
                <button
                  onClick={() => navigate(`/dashboard/clients/${client.id}/edit`)}
                  className="p-2 bg-[#1E3A5F] text-[#FFD700] rounded-lg hover:bg-[#2D5A8F] transition-all"
                  title="Edit"
                >
                  <FaEdit />
                </button>
                {/* Reset Password Button - only show if client has a user account */}
                {client.user_id && (
                  <ResetPasswordButton
                    userId={client.user_id}
                    userName={client.name}
                    userEmail={client.email || client.contact}
                    userRole="client"
                    resetEndpoint="advisor"
                    onSuccess={() => {}}
                    compact={true}
                    className="p-2 bg-[#1E3A5F] text-orange-400 rounded-lg hover:bg-orange-900/30 transition-all"
                  />
                )}
                <button
                  onClick={() => handleDelete(client.id)}
                  className="p-2 bg-[#1E3A5F] text-red-400 rounded-lg hover:bg-red-900 transition-all"
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Footer */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Total Clients</p>
            <p className="text-3xl font-bold text-[#FFD700] mt-1">{clients.length}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-400 text-sm">Showing</p>
            <p className="text-3xl font-bold text-white mt-1">{filteredClients.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsList;